/**
 * Configuration applicative résolue à l'EXÉCUTION, et non au build.
 *
 * Pourquoi : Vite remplace `import.meta.env.VITE_*` par des littéraux pendant
 * `vite build`. Utilisées directement, ces variables figent le domaine dans le
 * bundle : la même image Docker servie sur cra-dev.clippit.cloud renvoyait
 * l'utilisateur vers cra.clippit.fr (redirect MSAL) et interrogeait l'API de
 * production. Une image par environnement serait la seule issue.
 *
 * Ici, les valeurs sont résolues dans cet ordre de priorité :
 *
 *   1. `window.__CLIPPIT_CONFIG__` — injecté par /config.js, fichier généré au
 *      démarrage du conteneur (docker-entrypoint.sh) à partir des variables
 *      d'environnement du pod. C'est le mécanisme utilisé en Kubernetes.
 *   2. `import.meta.env.VITE_*` — développement local (`npm run dev`, fichier
 *      .env) et builds statiques hors conteneur.
 *   3. Valeurs par défaut déduites de l'URL courante (même origine).
 *
 * Conséquence : une seule image, N environnements. Aucun domaine n'est figé.
 */

export interface AppConfig {
    /** Origine de l'API. Chaîne vide = même origine que le front (cas Ingress). */
    apiUrl: string;
    azureClientId: string;
    azureTenantId: string;
    /** URI de retour MSAL. Par défaut l'origine courante : le front suit le domaine qui le sert. */
    azureRedirectUri: string;
    /** URL du portail client, pour les liens sortants depuis l'admin. */
    reportingUrl: string;
    /** Nom de l'environnement ("production", "dev", "preprod"...). Affiché dans l'UI hors production. */
    environment: string;
}

/** Clés acceptées dans /config.js (window.__CLIPPIT_CONFIG__). */
export type RuntimeConfigKey =
    | 'API_URL'
    | 'AZURE_CLIENT_ID'
    | 'AZURE_TENANT_ID'
    | 'AZURE_REDIRECT_URI'
    | 'REPORTING_URL'
    | 'ENVIRONMENT';

function readInjected(key: RuntimeConfigKey): string | undefined {
    if (typeof window === 'undefined') return undefined;
    return window.__CLIPPIT_CONFIG__?.[key];
}

function clean(value: string | undefined): string {
    if (typeof value !== 'string') return '';
    const trimmed = value.trim();
    // Un placeholder non substitué ("__API_URL__") ne doit jamais passer pour une valeur.
    if (!trimmed || trimmed.startsWith('__')) return '';
    return trimmed;
}

function pick(key: RuntimeConfigKey, buildTimeValue: string | undefined, fallback: string): string {
    return clean(readInjected(key)) || clean(buildTimeValue) || fallback;
}

function stripTrailingSlash(url: string): string {
    return url.replace(/\/+$/, '');
}

const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

export const config: AppConfig = {
    apiUrl: stripTrailingSlash(pick('API_URL', import.meta.env.VITE_API_URL, '')),
    azureClientId: pick('AZURE_CLIENT_ID', import.meta.env.VITE_AZURE_CLIENT_ID, ''),
    azureTenantId: pick('AZURE_TENANT_ID', import.meta.env.VITE_AZURE_TENANT_ID, 'common'),
    azureRedirectUri: stripTrailingSlash(
        pick('AZURE_REDIRECT_URI', import.meta.env.VITE_AZURE_REDIRECT_URI, currentOrigin)
    ),
    reportingUrl: stripTrailingSlash(pick('REPORTING_URL', import.meta.env.VITE_REPORTING_URL, '')),
    environment: pick('ENVIRONMENT', import.meta.env.VITE_APP_ENVIRONMENT, 'production'),
};

export const isProduction = config.environment === 'production';

/** Construit une URL d'API absolue ou relative selon la configuration. */
export function apiUrl(path: string): string {
    return `${config.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

// Sans client ID, MSAL échoue à l'initialisation avec une erreur peu parlante.
// On rend la cause explicite dans la console.
if (!config.azureClientId) {
    console.error(
        '[clippit-cra] APP_AZURE_CLIENT_ID absent : la connexion Entra ID va échouer. ' +
        'Renseigner la clé APP_AZURE_CLIENT_ID du ConfigMap de l\'environnement ' +
        '(Kubernet/overlays/<env>/kustomization.yml), ou VITE_AZURE_CLIENT_ID en local.'
    );
}

// Trace de démarrage : permet de vérifier d'un coup d'œil, dans la console du
// navigateur, sur quel environnement et quelle API on est réellement branché.
if (!isProduction) {
    console.info('[clippit-cra] configuration runtime', {
        environment: config.environment,
        apiUrl: config.apiUrl || '(même origine)',
        azureRedirectUri: config.azureRedirectUri,
        azureTenantId: config.azureTenantId,
    });
}
